import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Configuration du module d'abonnement
 *
 * SUBSCRIPTION_MODULE_ENABLED:
 * - true: Le système de restriction d'accès basé sur l'abonnement est actif
 *         Les utilisateurs sans abonnement premium ne peuvent accéder qu'au dashboard et à la page d'abonnement
 * - false: Le système de restriction est désactivé
 *          Tous les utilisateurs ont accès à toutes les fonctionnalités selon leurs permissions de rôle
 */

// Configuration par défaut (utilisée si Firestore n'est pas disponible)
const DEFAULT_CONFIG = {
  SUBSCRIPTION_MODULE_ENABLED: false,
  ALWAYS_ACCESSIBLE_ROUTES: [
    '/',
    '/dashboard',
    '/subscription',
    '/profile',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/update-email'
  ],
  ALWAYS_ACCESSIBLE_FEATURES: [
    'dashboard',
    'subscription',
    'profile'
  ]
};

// Cache de la configuration en mémoire
let configCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5000; // 5 secondes

/**
 * Récupère la configuration depuis Firestore
 */
export const getSubscriptionConfig = async () => {
  try {
    // Vérifier le cache
    if (configCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return configCache;
    }

    const configRef = doc(db, 'systemConfig', 'subscriptionModule');
    const configDoc = await getDoc(configRef);

    if (configDoc.exists()) {
      const config = configDoc.data();
      configCache = { ...DEFAULT_CONFIG, ...config };
      cacheTimestamp = Date.now();
      return configCache;
    } else {
      // Créer la configuration par défaut
      await setDoc(configRef, DEFAULT_CONFIG);
      configCache = DEFAULT_CONFIG;
      cacheTimestamp = Date.now();
      return DEFAULT_CONFIG;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la configuration:', error);
    return DEFAULT_CONFIG;
  }
};

/**
 * Met à jour la configuration dans Firestore
 */
export const updateSubscriptionConfig = async (updates) => {
  try {
    const configRef = doc(db, 'systemConfig', 'subscriptionModule');
    await setDoc(configRef, updates, { merge: true });

    // Invalider le cache
    configCache = null;
    cacheTimestamp = null;

    return { success: true };
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la configuration:', error);
    throw error;
  }
};

/**
 * Vérifie si le module d'abonnement est activé
 * Version synchrone pour la compatibilité (utilise le cache)
 */
export const isSubscriptionModuleEnabled = () => {
  if (configCache) {
    return configCache.SUBSCRIPTION_MODULE_ENABLED;
  }
  // Par défaut, retourner false si le cache n'est pas chargé
  return DEFAULT_CONFIG.SUBSCRIPTION_MODULE_ENABLED;
};

/**
 * Vérifie si une route est toujours accessible
 */
export const isAlwaysAccessibleRoute = (route) => {
  const config = configCache || DEFAULT_CONFIG;
  return config.ALWAYS_ACCESSIBLE_ROUTES.includes(route);
};

/**
 * Vérifie si une fonctionnalité est toujours accessible
 */
export const isAlwaysAccessibleFeature = (feature) => {
  const config = configCache || DEFAULT_CONFIG;
  return config.ALWAYS_ACCESSIBLE_FEATURES.includes(feature);
};
